import type { Room, Wall, Point } from '../types';
import { pointsAreEqual, getRoomCenter } from './floorPlanUtils';

export interface SharedWallSegment {
  id: string;
  start: Point;
  end: Point;
  sharedBetweenRooms: string[]; // Array of room stableIds
  isFullyShared: boolean; // true if entire segment is shared
  originalWallIds: string[]; // Track source walls
  wallType: string;
  floor?: number;
}

export interface SharedWallConnection {
  wallSegmentId: string;
  connectedRoomIds: string[];
  connectionType: 'shared' | 'touching' | 'independent';
  connectionStrength: number; // 0-1, how strong the connection is
}

export interface SharedWallSystemState {
  sharedSegments: SharedWallSegment[];
  connections: SharedWallConnection[];
  independentSegments: Wall[];
  roomConnectivity: Map<string, string[]>; // roomId -> connected room IDs
}

/**
 * Analyzes walls and rooms to detect shared wall segments
 * This uses professional CAD-style segment overlap detection
 * 
 * 🚫 DISABLED: Shared wall system is disabled - returns empty state
 */
export const analyzeSharedWallSystem = (
  walls: Wall[],
  rooms: Room[],
  tolerance: number = 0.1
): SharedWallSystemState => {
  // 🚫 DISABLED: Return empty state to disable shared wall system
  console.log('🚫 SHARED WALL SYSTEM: Disabled - returning empty state');
  
  return {
    sharedSegments: [],
    connections: [],
    independentSegments: walls, // All walls are independent when system is disabled
    roomConnectivity: new Map()
  };
};

/**
 * Determines how moving a shared wall affects connected rooms
 * This respects your existing room ID system
 */
/**
 * 🚫 DISABLED: Shared wall system is disabled - returns independent movement
 */
export const calculateSharedWallMovementEffect = (
  wallSegmentId: string,
  newStart: Point,
  newEnd: Point,
  systemState: SharedWallSystemState,
  rooms: Room[]
): {
  affectedRooms: string[];
  movementType: 'shared' | 'partial' | 'independent';
  preserveRoomIds: boolean;
} => {
  // 🚫 DISABLED: Return independent movement to disable shared wall system
  console.log('🚫 SHARED WALL SYSTEM: Disabled - returning independent movement');
  
  return {
    affectedRooms: [],
    movementType: 'independent',
    preserveRoomIds: true
  };
};

/**
 * Updates shared wall system state after room operations
 * This works with your existing room calculation and ID migration system
 * 
 * 🚫 DISABLED: Shared wall system is disabled - returns empty state
 */
export const updateSharedWallSystemAfterRoomChange = (
  oldSystemState: SharedWallSystemState,
  newWalls: Wall[],
  newRooms: Room[],
  roomMigrationMap: Record<string, string> // Your existing migration system
): SharedWallSystemState => {
  // 🚫 DISABLED: Return empty state to disable shared wall system
  console.log('🚫 SHARED WALL SYSTEM: Disabled - returning empty state for update');
  
  return {
    sharedSegments: [],
    connections: [],
    independentSegments: newWalls, // All walls are independent when system is disabled
    roomConnectivity: new Map()
  };
};

/**
 * Checks if a room resize operation would create or destroy shared walls
 * This integrates with your existing room resize logic
 */
export const analyzeRoomResizeSharedWallImpact = (
  resizingRoomId: string,
  newRoomWalls: Wall[],
  otherRooms: Room[],
  currentSystemState: SharedWallSystemState
): {
  newSharedWalls: SharedWallSegment[];
  removedSharedWalls: string[];
  modifiedSharedWalls: SharedWallSegment[];
  roomConnectionChanges: { added: string[]; removed: string[] };
} => {
  const newSharedWalls: SharedWallSegment[] = [];
  const removedSharedWalls: string[] = [];
  const modifiedSharedWalls: SharedWallSegment[] = [];
  const roomConnectionChanges = { added: [] as string[], removed: [] as string[] };

  // Create a temporary room for analysis
  const tempRoom: Room = {
    id: 'temp',
    stableId: resizingRoomId,
    walls: newRoomWalls,
    area: 0,
    name: 'temp',
    center: { x: 0, y: 0 },
    elements: []
  };

  // Analyze what would happen with the new room configuration
  const tempRooms = otherRooms.concat([tempRoom]);
  const tempSystemState = analyzeSharedWallSystem(
    newRoomWalls.concat(otherRooms.flatMap(r => r.walls)),
    tempRooms
  );

  console.log('🔗 ROOM RESIZE SHARED WALL IMPACT:', {
    resizingRoomId,
    currentSharedWalls: currentSystemState.sharedSegments.length,
    potentialNewSharedWalls: tempSystemState.sharedSegments.length,
    newRoomWallsCount: newRoomWalls.length
  });

  return {
    newSharedWalls,
    removedSharedWalls,
    modifiedSharedWalls,
    roomConnectionChanges
  };
};

// Helper functions - EXPORTED for consistent usage across the app
export const getWallKey = (wall: { start: Point; end: Point }): string => {
  const start = `${wall.start.x.toFixed(2)},${wall.start.y.toFixed(2)}`;
  const end = `${wall.end.x.toFixed(2)},${wall.end.y.toFixed(2)}`;
  return start < end ? `${start}-${end}` : `${end}-${start}`;
};

const generateSharedWallId = (wall: Wall): string => {
  return `shared_${getWallKey(wall)}_${Date.now()}`;
};

/**
 * 🎯 PROFESSIONAL CAD OVERLAP DETECTION
 * Find overlapping segments between two walls (not just identical walls)
 */
export const findWallOverlap = (
  wall1: Wall,
  wall2: Wall,
  tolerance: number = 0.1
): {
  hasOverlap: boolean;
  overlapType: 'none' | 'partial' | 'complete' | 'contained';
  overlapStart: Point;
  overlapEnd: Point;
  overlapLength: number;
} => {
  // Check if walls are collinear (on the same line)
  if (!areWallsCollinear(wall1, wall2, tolerance)) {
    return {
      hasOverlap: false,
      overlapType: 'none',
      overlapStart: { x: 0, y: 0 },
      overlapEnd: { x: 0, y: 0 },
      overlapLength: 0
    };
  }

  // Project both walls onto a common axis to find overlap
  const projection1 = projectWallOntoAxis(wall1);
  const projection2 = projectWallOntoAxis(wall2);

  // Find the intersection of the two projected segments
  const overlapStart = Math.max(projection1.min, projection2.min);
  const overlapEnd = Math.min(projection1.max, projection2.max);

  // Check if there's actual overlap
  if (overlapStart >= overlapEnd - tolerance) {
    return {
      hasOverlap: false,
      overlapType: 'none',
      overlapStart: { x: 0, y: 0 },
      overlapEnd: { x: 0, y: 0 },
      overlapLength: 0
    };
  }

  // Convert back to world coordinates
  const overlapWorldStart = projectAxisPointToWorld(overlapStart, wall1);
  const overlapWorldEnd = projectAxisPointToWorld(overlapEnd, wall1);
  const overlapLength = overlapEnd - overlapStart;

  // Determine overlap type
  let overlapType: 'partial' | 'complete' | 'contained' = 'partial';
  
  const wall1Length = projection1.max - projection1.min;
  const wall2Length = projection2.max - projection2.min;
  
  if (Math.abs(overlapLength - wall1Length) < tolerance && Math.abs(overlapLength - wall2Length) < tolerance) {
    overlapType = 'complete'; // Both walls completely overlap
  } else if (Math.abs(overlapLength - wall1Length) < tolerance || Math.abs(overlapLength - wall2Length) < tolerance) {
    overlapType = 'contained'; // One wall is contained within the other
  }

  return {
    hasOverlap: true,
    overlapType,
    overlapStart: overlapWorldStart,
    overlapEnd: overlapWorldEnd,
    overlapLength
  };
};

/**
 * Check if two walls are collinear (on the same line)
 */
const areWallsCollinear = (wall1: Wall, wall2: Wall, tolerance: number): boolean => {
  // Check if all four points are collinear
  const points = [wall1.start, wall1.end, wall2.start, wall2.end];
  
  // Use the first two points to define the line
  const [p1, p2] = points;
  
  // Check if the other two points are on the same line
  for (let i = 2; i < points.length; i++) {
    const distanceToLine = pointToLineDistance(points[i], p1, p2);
    if (distanceToLine > tolerance) {
      return false;
    }
  }
  
  return true;
};

/**
 * Project a wall onto a single axis for overlap calculation
 */
const projectWallOntoAxis = (wall: Wall): { min: number; max: number } => {
  // Use the dominant axis (x or y) based on wall orientation
  const deltaX = Math.abs(wall.end.x - wall.start.x);
  const deltaY = Math.abs(wall.end.y - wall.start.y);
  
  if (deltaX >= deltaY) {
    // Horizontal-ish wall, project onto X axis
    return {
      min: Math.min(wall.start.x, wall.end.x),
      max: Math.max(wall.start.x, wall.end.x)
    };
  } else {
    // Vertical-ish wall, project onto Y axis
    return {
      min: Math.min(wall.start.y, wall.end.y),
      max: Math.max(wall.start.y, wall.end.y)
    };
  }
};

/**
 * Convert axis projection back to world coordinates
 */
const projectAxisPointToWorld = (axisValue: number, referenceWall: Wall): Point => {
  const deltaX = Math.abs(referenceWall.end.x - referenceWall.start.x);
  const deltaY = Math.abs(referenceWall.end.y - referenceWall.start.y);
  
  if (deltaX >= deltaY) {
    // Horizontal-ish wall, interpolate on X axis
    const t = (axisValue - Math.min(referenceWall.start.x, referenceWall.end.x)) / deltaX;
    return {
      x: axisValue,
      y: referenceWall.start.y + t * (referenceWall.end.y - referenceWall.start.y)
    };
  } else {
    // Vertical-ish wall, interpolate on Y axis
    const t = (axisValue - Math.min(referenceWall.start.y, referenceWall.end.y)) / deltaY;
    return {
      x: referenceWall.start.x + t * (referenceWall.end.x - referenceWall.start.x),
      y: axisValue
    };
  }
};

/**
 * Find which rooms contain a specific wall
 */
export const findRoomsUsingWall = (
  targetWall: Wall,
  rooms: Room[],
  tolerance: number = 0.1
): string[] => {
  const roomIds: string[] = [];
  
  for (const room of rooms) {
    if (!room.stableId || !room.walls) continue;
    
    const hasWall = room.walls.some(roomWall => 
      wallsAreEquivalent(targetWall, roomWall, tolerance)
    );
    
    if (hasWall) {
      roomIds.push(room.stableId);
    }
  }
  
  return roomIds;
};

/**
 * Check if two walls are equivalent (same or reverse direction)
 */
const wallsAreEquivalent = (wall1: Wall, wall2: Wall, tolerance: number): boolean => {
  const pointsEqual = (p1: Point, p2: Point) => 
    Math.abs(p1.x - p2.x) < tolerance && Math.abs(p1.y - p2.y) < tolerance;
  
  // Check same direction
  if (pointsEqual(wall1.start, wall2.start) && pointsEqual(wall1.end, wall2.end)) {
    return true;
  }
  
  // Check reverse direction
  if (pointsEqual(wall1.start, wall2.end) && pointsEqual(wall1.end, wall2.start)) {
    return true;
  }
  
  return false;
};

/**
 * Calculate distance from point to line segment
 */
const pointToLineDistance = (point: Point, lineStart: Point, lineEnd: Point): number => {
  const A = point.x - lineStart.x;
  const B = point.y - lineStart.y;
  const C = lineEnd.x - lineStart.x;
  const D = lineEnd.y - lineStart.y;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  
  if (lenSq === 0) {
    // Line start and end are the same point
    return Math.hypot(A, B);
  }
  
  const param = dot / lenSq;
  let xx: number, yy: number;

  if (param < 0) {
    xx = lineStart.x;
    yy = lineStart.y;
  } else if (param > 1) {
    xx = lineEnd.x;
    yy = lineEnd.y;
  } else {
    xx = lineStart.x + param * C;
    yy = lineStart.y + param * D;
  }

  const dx = point.x - xx;
  const dy = point.y - yy;
  return Math.hypot(dx, dy);
};

/**
 * Integration function for your existing wall splitting system
 * This preserves all your current functionality while adding shared wall awareness
 * 
 * 🚫 DISABLED: Shared wall system is disabled - returns non-shared split info
 */
export const integrateWithExistingWallSplit = (
  systemState: SharedWallSystemState,
  selectedWall: Wall,
  splitPoint: Point,
  roomsAffectedBySplit: Array<{ room: Room, originalLabel?: string }>
): {
  enhancedSplitInfo: {
    isSharedWall: boolean;
    affectedSharedSegments: string[];
    preserveAllLabels: boolean;
  };
  updatedSystemState: SharedWallSystemState;
} => {
  // 🚫 DISABLED: Return non-shared split info to disable shared wall system
  console.log('🚫 SHARED WALL SYSTEM: Disabled - returning non-shared split info');
  
  const enhancedSplitInfo = {
    isSharedWall: false, // Never shared when system is disabled
    affectedSharedSegments: [],
    preserveAllLabels: true // Always preserve labels in your system
  };

  return {
    enhancedSplitInfo,
    updatedSystemState: systemState // Return unchanged state
  };
}; 